/**
 * 報酬・通話料の設定(2026-09-24 発注者指示)。設定 → 報酬・通話料 で管理者が入れる。
 *  - インセンティブ: 月間の取得件数に応じた段階(100件1,000円 … 500件15,000円)。対象は全アポインター
 *  - 通話料の発信番号: アジャストワンのCSVの「ご利用番号」→ 会社。**同じ番号を別の会社へ引き継ぐことがある**ので
 *    `from`(適用開始日)を持ち、その日以降はその会社に付ける(空欄＝最初から)
 * 保存先は `settings/incentive` と `settings/call_numbers`(既存の settings コレクション。ルールの変更なし)。
 */
import { doc, getDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import { getDb } from '@core/lib/firebase';
import type { DateStr } from '@core/lib/types';

// ---- インセンティブ -------------------------------------------------------------

/** 月間の取得件数が `count` 件以上なら `amount` 円。件数の小さい順に持つ(判定は大きい方から) */
export type IncentiveTier = { count: number; amount: number };
export type IncentiveSettings = { enabled: boolean; tiers: IncentiveTier[] };

export const DEFAULT_INCENTIVE: IncentiveSettings = {
  enabled: true,
  tiers: [
    { count: 100, amount: 1000 },
    { count: 200, amount: 3000 },
    { count: 300, amount: 5000 },
    { count: 400, amount: 7000 },
    { count: 500, amount: 15000 },
  ],
};

/** その月の取得件数に対する支給額(段階のうち、条件を満たす一番上のもの) */
export function incentiveOf(appointments: number, s: IncentiveSettings = DEFAULT_INCENTIVE): number {
  if (!s.enabled) return 0;
  let amount = 0;
  for (const t of [...s.tiers].sort((a, b) => a.count - b.count)) if (appointments >= t.count) amount = t.amount;
  return amount;
}

export function normalizeIncentive(data: Record<string, unknown> | undefined): IncentiveSettings {
  const d = data ?? {};
  const tiers = Array.isArray(d.tiers) ? (d.tiers as IncentiveTier[]) : DEFAULT_INCENTIVE.tiers;
  return {
    enabled: d.enabled !== false,
    tiers: tiers
      .filter((t) => Number.isFinite(Number(t?.count)) && Number.isFinite(Number(t?.amount)))
      .map((t) => ({ count: Number(t.count), amount: Number(t.amount) }))
      .sort((a, b) => a.count - b.count),
  };
}

export async function fetchIncentive(): Promise<IncentiveSettings> {
  return normalizeIncentive((await getDoc(doc(getDb(), 'settings', 'incentive'))).data());
}

export async function saveIncentive(v: IncentiveSettings, userId: string): Promise<void> {
  await setDoc(doc(getDb(), 'settings', 'incentive'), { ...v, updated_by: userId, updated_at: serverTimestamp() }, { merge: true });
}

// ---- 通話料の発信番号 -----------------------------------------------------------

export type CallNumber = {
  /** アジャストワンのCSVの「ご利用番号」 */
  number: string;
  /** 会社マスタのID。空 = CC事業部の対象外(社外・自社番号など) */
  company_id: string;
  /** 会社マスタに無いときの表示名(対象外の番号や、まだ登録していない会社) */
  label: string;
  /** この日から この会社。空 = 最初から */
  from: DateStr | '';
  note: string;
};
export type CallNumberSettings = { items: CallNumber[] };

const digits = (s: string) => String(s ?? '').replace(/[^0-9]/g, '');

export function normalizeCallNumbers(data: Record<string, unknown> | undefined): CallNumberSettings {
  const list = Array.isArray(data?.items) ? (data!.items as CallNumber[]) : [];
  return {
    items: list
      .filter((x) => digits(x?.number) !== '')
      .map((x) => ({
        number: String(x.number).trim(),
        company_id: String(x.company_id ?? ''),
        label: String(x.label ?? ''),
        from: (String(x.from ?? '') as DateStr | ''),
        note: String(x.note ?? ''),
      }))
      .sort((a, b) => a.number.localeCompare(b.number) || String(a.from).localeCompare(String(b.from))),
  };
}

/**
 * その日のその番号がどの会社か(適用開始日が その日以前 のうち、一番新しいもの)。
 * 見つからない・対象外なら null
 */
export function companyOfNumber(items: CallNumber[], number: string, date: DateStr): string | null {
  const key = digits(number);
  const hit = items
    .filter((x) => digits(x.number) === key && (x.from === '' || x.from <= date))
    .sort((a, b) => String(a.from).localeCompare(String(b.from)))
    .pop();
  return hit && hit.company_id ? hit.company_id : null;
}

/** その番号の用途(label)。無ければ空 */
export function labelOfNumber(items: CallNumber[], number: string): string {
  const key = digits(number);
  const hit = items.filter((x) => digits(x.number) === key).pop();
  return hit?.label ?? '';
}

export async function fetchCallNumbers(): Promise<CallNumberSettings> {
  return normalizeCallNumbers((await getDoc(doc(getDb(), 'settings', 'call_numbers'))).data());
}

export async function saveCallNumbers(v: CallNumberSettings, userId: string): Promise<void> {
  await setDoc(doc(getDb(), 'settings', 'call_numbers'), { items: v.items, updated_by: userId, updated_at: serverTimestamp() }, { merge: true });
}
